import React, { useEffect, useState } from "react";
import { ProDeliveryDelay } from "@/components/ProDeliveryDelay";
import { feeLabel } from "@/lib/proApi";
import { Link } from "react-router-dom";
import { Trash2, ArrowLeft, FileDown, Loader2, Tag } from "lucide-react";
import { useMyCode } from "@/components/ProMyCode";
import { toast } from "sonner";
import { usePro } from "@/context/ProContext";
import { eur, imgSrc, errMsg, proApi } from "@/lib/proApi";
import { Stepper } from "@/pages/pro/ProProduct";

export const Totals = ({ q, shippingLabel }) => (
  <div className="space-y-2 text-sm" data-testid="pro-totals">
    <div className="flex justify-between"><span className="text-[var(--oz-muted)]">Sous-total produits HT</span><span data-testid="pro-subtotal-ht">{eur(q.subtotal_ht_cents)}</span></div>
    {q.discount_ht_cents > 0 && <div className="flex justify-between text-[#8A6A2E] font-semibold"><span>Remise {q.promo_code} -{q.discount_percent} %</span><span data-testid="pro-discount-ht">- {eur(q.discount_ht_cents)}</span></div>}
    <div className="flex justify-between"><span className="text-[var(--oz-muted)]">{q.vat_mode === "dom_tom" ? "Livraison DOM-TOM" : `Livraison HT${shippingLabel ? ` — ${shippingLabel}` : ""}`}</span><span data-testid="pro-shipping-ht">{q.shipping_method ? eur(q.shipping_ht_cents) : "à choisir"}</span></div>
    <div className="flex justify-between"><span className="text-[var(--oz-muted)]">Total HT</span><span data-testid="pro-grand-ht">{eur(q.total_ht_cents)}</span></div>
    <div className="flex justify-between"><span className="text-[var(--oz-muted)]">{q.vat_mode === "dom_tom" ? "TVA non applicable (DOM-TOM)" : `TVA ${String(q.vat_rate).replace(".", ",")} %${q.vat_mode === "reverse_charge" ? " (autoliquidation)" : ""}`}</span><span data-testid="pro-vat">{eur(q.vat_cents)}</span></div>
    <div className="flex justify-between pt-2 border-t border-[var(--oz-border)] text-base font-semibold"><span>Total TTC</span><span data-testid="pro-total-ttc-cart">{eur(q.total_ttc_cents)}</span></div>
  </div>
);

const PromoBox = () => {
  const { cart, setCart } = usePro();
  const mine = useMyCode();
  const [code, setCode] = useState("");
  useEffect(() => { if (mine?.code) setCode((c) => c || mine.code); }, [mine]);
  const [busy, setBusy] = useState(false);
  const apply = async (value) => {
    setBusy(true);
    try {
      const { data } = await proApi.put("/pro/cart/promo", { code: value });
      setCart(data);
      if (value) toast.success(`Code ${data.promo_code} appliqué : -${data.discount_percent} %`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (cart?.promo_code) return (
    <p data-testid="pro-promo-applied" className="mb-4 flex items-center justify-between rounded-xl bg-[var(--oz-gold)]/15 px-3 py-2 text-xs font-semibold">
      <span className="inline-flex items-center gap-1.5"><Tag className="w-3.5 h-3.5 text-[var(--oz-gold)]" /> Code {cart.promo_code} appliqué</span>
      {!cart.quote_number && <button onClick={() => apply("")} data-testid="pro-promo-remove" className="underline text-[var(--oz-muted)]">retirer</button>}
    </p>
  );
  if (cart?.quote_number) return null;
  return (
    <div data-testid="pro-promo-box" className="mb-5">
      {mine?.code && <p data-testid="pro-promo-reminder" className="mb-2 flex items-center gap-2 rounded-xl bg-[var(--oz-gold)] text-[var(--oz-dark)] px-3 py-2 text-xs font-semibold">
        <Tag className="w-4 h-4 shrink-0" /> -{mine.percent} % avec votre code personnel {mine.code} : cliquez sur « Appliquer »
      </p>}
      <div className="flex gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value)} data-testid="pro-promo-input" placeholder="Code promo"
          className="flex-1 min-w-0 rounded-full border border-[var(--oz-border)] px-4 py-2.5 text-sm outline-none focus:border-[var(--oz-gold)]" />
        <button onClick={() => apply(code.trim())} disabled={busy || !code.trim()} data-testid="pro-promo-apply"
          className="px-4 py-2.5 rounded-full bg-[var(--oz-dark)] text-white text-sm font-semibold disabled:opacity-60">{busy ? "..." : "Appliquer"}</button>
      </div>
    </div>
  );
};

const QuoteBox = ({ config, cart }) => {
  const [ship, setShip] = useState("mondial_relay");
  const [busy, setBusy] = useState(false);
  const noDelivery = cart && !cart.min_delivery_ok;
  useEffect(() => { if (noDelivery) setShip("pickup"); }, [noDelivery]);
  const make = async () => {
    const win = window.open("", "_blank");
    setBusy(true);
    try {
      const { data } = await proApi.post("/pro/quotes", { shipping_method: ship });
      if (win) win.location.href = data.url; else window.location.href = data.url;
      toast.success(`Devis ${data.number} créé — retrouvez-le dans votre compte`);
    } catch (e) { if (win) win.close(); toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="pro-quote-box" className="mt-5 pt-5 border-t border-[var(--oz-border)]">
      <p className="text-sm font-semibold">Besoin d'un devis ?</p>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {[["mondial_relay", "Mondial Relay", config?.mondial_relay_fee_ht ? `${config.mondial_relay_fee_ht} € HT` : "Gratuit"],
          ["carrier", "Transporteur", `${feeLabel(config?.carrier_fee_ht, "Gratuit")} · DOM-TOM 30 à 60 €`], ["pickup", "Retrait au dépôt", "Chatou (78) — gratuit"]].map(([k, t, d]) => (
          <button key={k} type="button" onClick={() => setShip(k)} data-testid={`pro-quote-ship-${k}`} disabled={noDelivery && k !== "pickup"}
            className={`rounded-xl border px-3 py-2 text-left text-xs disabled:opacity-40 ${ship === k ? "border-[var(--oz-dark)] ring-1 ring-[var(--oz-dark)]" : "border-[var(--oz-border)]"}`}>
            <span className="block font-semibold">{t}</span><span className="text-[var(--oz-muted)]">{d}</span>
          </button>
        ))}
      </div>
      <button onClick={make} disabled={busy} data-testid="pro-quote-download" className="mt-3 w-full inline-flex justify-center items-center gap-2 px-5 py-3 rounded-full border border-[var(--oz-dark)] text-sm font-semibold disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Télécharger un devis PDF
      </button>
      <p className="mt-2 text-[11px] text-[var(--oz-muted)]">Devis gratuit, valable {config?.quote_validity_days ?? 15} jours.</p>
    </div>
  );
};

export default function ProCart() {
  const { cart, setLots, removeItem, config } = usePro();
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!cart) return <div className="py-24" />;
  const empty = !cart.lines.length;

  return (
    <div data-testid="pro-cart" className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-16 py-10">
      <h1 className="font-serif text-4xl sm:text-5xl text-[var(--oz-text)]">Panier professionnel</h1>
      {empty ? (
        <div className="mt-8 rounded-2xl bg-white border border-[var(--oz-border)] p-10 text-center">
          <p data-testid="pro-cart-empty" className="text-[var(--oz-muted)]">Votre panier est vide.</p>
          <Link to="/espace-pro" className="mt-4 inline-block px-6 py-3 rounded-full bg-[var(--oz-dark)] text-white font-semibold text-sm">Voir le catalogue</Link>
        </div>
      ) : (
        <div className="mt-8 grid lg:grid-cols-[1fr_360px] gap-8 items-start">
          <div className="space-y-3">
            {cart.quote_number && <p data-testid="pro-cart-quote" className="rounded-xl bg-[#FBF6EC] border border-[var(--oz-gold)] px-4 py-3 text-sm">Panier issu du devis <strong>{cart.quote_number}</strong> (prix du devis). Modifier le panier applique les prix actuels du catalogue.</p>}
            {cart.lines.map((l) => {
              const id = `${l.slug}-${l.pack_size}`;
              return (
                <div key={id} data-testid={`pro-cart-line-${id}`} className={`rounded-2xl bg-white border border-[var(--oz-border)] p-4 flex gap-4 ${busy ? "opacity-70" : ""}`}>
                  <img src={imgSrc(l.image)} alt={l.name} className="w-20 h-20 rounded-xl object-cover bg-[var(--oz-bg-2)] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <p className="font-semibold text-[var(--oz-text)]">{l.name}</p>
                      <button aria-label="Supprimer" data-testid={`pro-cart-remove-${id}`} disabled={busy} onClick={() => run(() => removeItem(l.product_id, l.pack_size))} className="p-1.5 text-[var(--oz-muted)] hover:text-[var(--oz-badge)]"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <p className="text-xs text-[var(--oz-muted)] mt-0.5">{l.pack_size} pièces • {eur(l.unit_ht_cents)} HT / pièce</p>
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                      <Stepper value={l.lots} onChange={(v) => run(() => setLots(l.product_id, l.pack_size, v))} testid={`pro-cart-lots-${id}`} />
                      <div className="text-right text-sm">
                        <p className="text-[var(--oz-muted)]" data-testid={`pro-cart-pieces-${id}`}>{l.pieces} pièces</p>
                        <p className="font-semibold" data-testid={`pro-cart-line-total-${id}`}>{eur(l.total_ht_cents)} HT</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <Link to="/espace-pro" data-testid="pro-continue-shopping" className="inline-flex items-center gap-1.5 text-sm font-semibold py-2"><ArrowLeft className="w-4 h-4" /> Continuer mes achats</Link>
          </div>
          <aside className="rounded-2xl bg-white border border-[var(--oz-border)] p-6 lg:sticky lg:top-36">
            <PromoBox />
            <Totals q={cart} />
            <p className="mt-3 text-xs text-[var(--oz-muted)]">Livraison Mondial Relay {feeLabel(config?.mondial_relay_fee_ht, "offerte")} ou transporteur {feeLabel(config?.carrier_fee_ht, "offert")} (France métropolitaine), à choisir à l'étape suivante.</p>
            {config?.min_delivery_msg && <p data-testid="pro-cart-min-delivery" className={`mt-1 text-xs ${cart.min_delivery_ok ? "text-[var(--oz-muted)]" : "text-[var(--oz-badge)] font-semibold"}`}>{config.min_delivery_msg}</p>}
            {config?.dom_tom_fees && <p data-testid="pro-cart-dom-fees" className="mt-1 text-xs text-[var(--oz-muted)]">{config.dom_tom_fees}. {config.belgium_fees}.</p>}
            <ProDeliveryDelay className="mt-2" testid="pro-cart-delay" />
            <Link to="/espace-pro/commande" data-testid="pro-checkout-btn" className="mt-5 block text-center px-6 py-3.5 rounded-full bg-[var(--oz-gold)] text-[var(--oz-dark)] font-semibold">Passer commande</Link>
            <QuoteBox config={config} cart={cart} />
          </aside>
        </div>
      )}
    </div>
  );
}
